import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileQuestion,
  Loader2,
  RefreshCw,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import type { Contractor } from "@/data/types";
import type {
  CanonicalOnboardingReviewContext,
  CanonicalOnboardingReviewSubject,
} from "@/data/contractor/onboarding/canonicalComplianceReviewContext";
import {
  CANONICAL_INTERNAL_COMPLIANCE_NOTE_MAX_LENGTH,
  CANONICAL_INTERNAL_COMPLIANCE_REVIEW_REQUIREMENT_KEYS,
  CANONICAL_INTERNAL_COMPLIANCE_REVIEW_STATES,
  type CanonicalInternalComplianceReviewDecision,
  type CanonicalInternalComplianceReviewDetail,
  type CanonicalInternalComplianceReviewHistory,
  type CanonicalInternalComplianceReviewQueue,
  type CanonicalInternalComplianceReviewQueueItem,
  type CanonicalInternalComplianceReviewRequirementKey,
  type CanonicalInternalComplianceReviewState,
  type CanonicalInternalComplianceReviewPreview,
} from "@/data/contractor/compliance/canonicalInternalComplianceReview";
import type {
  CanonicalIndividualComplianceRead,
  CanonicalIndividualComplianceRequirement,
} from "@/data/contractor/compliance/canonicalIndividualCompliance";
import {
  CanonicalInternalComplianceReviewClientError,
  createCanonicalInternalComplianceReviewClient,
} from "@/services/individual-compliance-ops-review.service";
import {
  canonicalIndividualComplianceClient,
} from "@/services/individual-compliance.service";
import {
  createCanonicalInternalComplianceReviewPreviewClient,
} from "@/services/individual-compliance-ops-review-preview";
import {
  CANONICAL_COMPLIANCE_DECISION_LABELS,
  CANONICAL_COMPLIANCE_REQUIREMENT_LABELS,
  CANONICAL_COMPLIANCE_REVIEW_ERROR_MESSAGES,
  CANONICAL_COMPLIANCE_REVIEW_REASONS,
  CANONICAL_COMPLIANCE_REVIEW_STATE_LABELS,
  buildCanonicalComplianceOrganizationOptions,
  getCanonicalComplianceReasonLabel,
  getCanonicalComplianceReviewErrorMessage,
  remainingCanonicalComplianceNoteCharacters,
  validateCanonicalComplianceDecisionDraft,
  type CanonicalComplianceReviewReasonCode,
} from "./canonicalComplianceReviewUi";

type ReviewClient = ReturnType<
  typeof createCanonicalInternalComplianceReviewClient
>;
type PreviewClient = ReturnType<
  typeof createCanonicalInternalComplianceReviewPreviewClient
>;
type ManagedComplianceClient = Pick<
  typeof canonicalIndividualComplianceClient,
  "getManagedCrew"
>;

const defaultReviewClient = createCanonicalInternalComplianceReviewClient();
const defaultPreviewClient =
  createCanonicalInternalComplianceReviewPreviewClient();
const defaultManagedComplianceClient = canonicalIndividualComplianceClient;
const PAGE_LIMIT = 25;
const ALL_FILTER_VALUE = "all";

type QueueState =
  | "selection_required"
  | "loading"
  | "ready"
  | "empty"
  | "error";

type DecisionDraft = Readonly<{
  evidenceId: string;
  requirementKey: CanonicalInternalComplianceReviewRequirementKey;
  decision: CanonicalInternalComplianceReviewDecision;
}>;

type TrustedReviewSubject = Readonly<{
  label: string;
  relationshipLabel: string;
  groupLabel: "Parent personnel" | "Supported subcontractor link" | "Crew";
  authoritySource: "direct_parent_roster" | "delegation_candidate" | "ambiguous";
}>;

const IDENTITY_UNAVAILABLE_LABEL = "Identity unavailable";

function safeDisplayLabel(value: string | null | undefined): string | null {
  const normalized = String(value ?? "").trim();
  return normalized || null;
}

function buildTrustedSubjectMap(
  context: CanonicalOnboardingReviewContext | undefined,
): ReadonlyMap<string, TrustedReviewSubject> {
  const subjects = new Map<string, TrustedReviewSubject>();
  if (!context) return subjects;

  const groups = [
    {
      groupLabel: "Parent personnel" as const,
      subjects: context.parentPersonnel,
    },
    {
      groupLabel: "Supported subcontractor link" as const,
      subjects: context.subcontractors,
    },
    {
      groupLabel: "Crew" as const,
      subjects: context.crew,
    },
  ];

  for (const group of groups) {
    for (const subject of group.subjects) {
      const label = safeDisplayLabel(subject.label);
      if (!label) continue;
      const next: TrustedReviewSubject = Object.freeze({
        label,
        relationshipLabel:
          safeDisplayLabel(subject.relationshipLabel) ?? group.groupLabel,
        groupLabel: group.groupLabel,
        authoritySource: "direct_parent_roster",
      });
      const existing = subjects.get(subject.targetPersonUserId);
      if (!existing) {
        subjects.set(subject.targetPersonUserId, next);
        continue;
      }
      if (existing.label !== next.label) {
        subjects.set(subject.targetPersonUserId, Object.freeze({
          label: IDENTITY_UNAVAILABLE_LABEL,
          relationshipLabel: "Ambiguous onboarding identity",
          groupLabel: existing.groupLabel,
          authoritySource: "ambiguous",
        }));
      }
    }
  }

  for (const contractor of context.relatedContractors) {
    if (contractor.depth !== 1) continue;
    for (const person of contractor.linkedPeople) {
      if (
        !person.userId
        || !person.childRosterActive
      ) {
        continue;
      }
      const label = safeDisplayLabel(person.displayName);
      if (!label) continue;
      const next: TrustedReviewSubject = Object.freeze({
        label,
        relationshipLabel: contractor.relationshipLabel,
        groupLabel: "Supported subcontractor link",
        authoritySource: "delegation_candidate",
      });
      const existing = subjects.get(person.userId);
      if (!existing) {
        subjects.set(person.userId, next);
      } else if (existing.label !== next.label) {
        subjects.set(person.userId, Object.freeze({
          label: IDENTITY_UNAVAILABLE_LABEL,
          relationshipLabel: "Ambiguous onboarding identity",
          groupLabel: existing.groupLabel,
          authoritySource: "ambiguous",
        }));
      }
    }
  }
  return subjects;
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "Unavailable"
    : parsed.toLocaleString();
}

function formatBytes(value: number | null): string {
  if (value === null) return "—";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function formatFailureReason(value: string | null): string {
  if (!value) return "Additional evidence conditions are unresolved.";
  return value
    .split("_")
    .filter(Boolean)
    .map((part, index) =>
      index === 0
        ? `${part.charAt(0).toUpperCase()}${part.slice(1)}`
        : part
    )
    .join(" ");
}

function sourceLabel(item: CanonicalInternalComplianceReviewQueueItem): string {
  return item.source.source === "canonical"
    ? "Canonical evidence"
    : "No canonical submission";
}

function queueItemKey(item: CanonicalInternalComplianceReviewQueueItem): string {
  const evidence = item.source.source === "canonical"
    ? item.source.evidenceId
    : "none";
  return `${item.targetPersonUserId ?? "unknown"}:${item.requirementKey}:${evidence}`;
}

function requirementStatusLabel(
  requirement: Pick<
    CanonicalIndividualComplianceRequirement,
    "documentSource" | "evidenceEligible" | "reviewState" | "operationallyVerified"
  >,
): string {
  return requirement.documentSource.source === "none"
    ? "No canonical submission"
    : `${requirement.evidenceEligible ? "Eligible" : "Not eligible"} · ${CANONICAL_COMPLIANCE_REVIEW_STATE_LABELS[requirement.reviewState]} · ${requirement.operationallyVerified ? "Operationally verified" : "Not operationally verified"}`;
}

function createIdempotencyKey(): string {
  if (
    typeof crypto !== "undefined"
    && typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  throw new Error("secure_idempotency_unavailable");
}

function errorKind(error: unknown): CanonicalInternalComplianceReviewClientError["kind"] | null {
  return error instanceof CanonicalInternalComplianceReviewClientError
    ? error.kind
    : null;
}

function queueErrorTitle(
  kind: CanonicalInternalComplianceReviewClientError["kind"] | null,
): string {
  if (kind === "authentication_required") return "Session expired";
  if (kind === "internal_review_forbidden") return "Review permission required";
  if (kind === "not_found") return "Compliance review unavailable";
  return "Canonical reviewer service unavailable";
}

export type CanonicalIndividualComplianceReviewPanelProps = Readonly<{
  contractors: readonly Contractor[];
  fixedContractorId?: string;
  scopeLabel?: string;
  onboardingContext?: CanonicalOnboardingReviewContext;
  contextKey?: string;
  reviewClient?: ReviewClient;
  previewClient?: PreviewClient;
  managedComplianceClient?: ManagedComplianceClient | null;
}>;

export function CanonicalIndividualComplianceReviewPanel({
  contractors,
  fixedContractorId,
  scopeLabel,
  onboardingContext,
  contextKey,
  reviewClient = defaultReviewClient,
  previewClient = defaultPreviewClient,
  managedComplianceClient = defaultManagedComplianceClient,
}: CanonicalIndividualComplianceReviewPanelProps) {
  const { toast } = useToast();
  const organizations = useMemo(
    () => buildCanonicalComplianceOrganizationOptions(contractors),
    [contractors],
  );
  const [organizationId, setOrganizationId] = useState(
    onboardingContext?.organizationId ?? "",
  );
  const effectiveFixedContractorId =
    onboardingContext?.parentContractorId ?? fixedContractorId;
  const [contractorId, setContractorId] = useState(
    effectiveFixedContractorId ?? "",
  );
  const [requirementKey, setRequirementKey] =
    useState<CanonicalInternalComplianceReviewRequirementKey | "">("");
  const [reviewStatus, setReviewStatus] =
    useState<CanonicalInternalComplianceReviewState | "">("");
  const [cursorHistory, setCursorHistory] = useState<readonly (string | null)[]>([
    null,
  ]);
  const [pageIndex, setPageIndex] = useState(0);
  const [queue, setQueue] =
    useState<CanonicalInternalComplianceReviewQueue | null>(null);
  const [queueState, setQueueState] =
    useState<QueueState>("selection_required");
  const [queueError, setQueueError] = useState("");
  const [queueErrorKind, setQueueErrorKind] =
    useState<CanonicalInternalComplianceReviewClientError["kind"] | null>(null);
  const [managedComplianceByUserId, setManagedComplianceByUserId] =
    useState<ReadonlyMap<string, CanonicalIndividualComplianceRead>>(new Map());
  const [selectedItem, setSelectedItem] =
    useState<CanonicalInternalComplianceReviewQueueItem | null>(null);
  const [detail, setDetail] =
    useState<CanonicalInternalComplianceReviewDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [history, setHistory] =
    useState<CanonicalInternalComplianceReviewHistory | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [preview, setPreview] =
    useState<CanonicalInternalComplianceReviewPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [decisionDraft, setDecisionDraft] = useState<DecisionDraft | null>(null);
  const [reasonCode, setReasonCode] =
    useState<CanonicalComplianceReviewReasonCode | "">("");
  const [note, setNote] = useState("");
  const [decisionError, setDecisionError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [attemptKey, setAttemptKey] = useState<string | null>(null);
  const [conflictRefreshing, setConflictRefreshing] = useState(false);
  const queueGeneration = useRef(0);
  const managedComplianceGeneration = useRef(0);
  const detailGeneration = useRef(0);
  const previewGeneration = useRef(0);
  const queueInFlight = useRef(false);
  const trustedSubjectMap = useMemo(
    () => buildTrustedSubjectMap(onboardingContext),
    [onboardingContext],
  );
  const hasOnboardingReviewableSubjects =
    !onboardingContext
    || [...trustedSubjectMap.values()].some(
      (subject) => subject.authoritySource !== "ambiguous",
    );

  const contractorOptions = useMemo(
    () => contractors
      .filter((contractor) => contractor.organizationId === organizationId)
      .slice()
      .sort((a, b) => a.companyName.localeCompare(b.companyName)),
    [contractors, organizationId],
  );
  const managedTargetUserIds = useMemo(() => {
    if (!onboardingContext) return [] as readonly string[];
    const ids = new Set<string>();
    for (const subject of [
      ...onboardingContext.parentPersonnel,
      ...onboardingContext.subcontractors,
      ...onboardingContext.crew,
    ]) {
      ids.add(subject.targetPersonUserId);
    }
    for (const contractor of onboardingContext.relatedContractors) {
      for (const person of contractor.linkedPeople) {
        if (person.userId) ids.add(person.userId);
      }
    }
    return Object.freeze([...ids]);
  }, [onboardingContext]);

  const resetSelection = useCallback(() => {
    detailGeneration.current += 1;
    previewGeneration.current += 1;
    setSelectedItem(null);
    setDetail(null);
    setDetailError("");
    setHistory(null);
    setHistoryError("");
    setPreview(null);
    setPreviewError("");
    setDecisionDraft(null);
    setReasonCode("");
    setNote("");
    setDecisionError("");
    setAttemptKey(null);
    setConflictRefreshing(false);
  }, []);

  const resetPaginationAndSelection = useCallback(() => {
    setCursorHistory((current) =>
      current.length === 1 && current[0] === null ? current : [null]
    );
    setPageIndex((current) => current === 0 ? current : 0);
    resetSelection();
  }, [resetSelection]);

  useEffect(() => {
    setOrganizationId(onboardingContext?.organizationId ?? "");
    setContractorId(effectiveFixedContractorId ?? "");
    setQueue(null);
    setQueueState(
      onboardingContext?.organizationId ? "loading" : "selection_required",
    );
    resetPaginationAndSelection();
  }, [
    contextKey,
    effectiveFixedContractorId,
    onboardingContext?.organizationId,
    resetPaginationAndSelection,
  ]);

  const loadQueue = useCallback(async (
    preserveExistingOnError = false,
  ): Promise<
    CanonicalInternalComplianceReviewQueue | null
  > => {
    if (
      !organizationId
      || !hasOnboardingReviewableSubjects
      || queueInFlight.current
    ) {
      return null;
    }
    const generation = ++queueGeneration.current;
    queueInFlight.current = true;
    setQueueState("loading");
    setQueueError("");
    setQueueErrorKind(null);
    try {
      const result = await reviewClient.listQueue({
        organizationId,
        ...(contractorId ? { contractorId } : {}),
        ...(requirementKey ? { requirementKey } : {}),
        ...(reviewStatus ? { reviewStatus } : {}),
        ...(cursorHistory[pageIndex]
          ? { cursor: cursorHistory[pageIndex] as string }
          : {}),
        limit: PAGE_LIMIT,
      });
      if (generation !== queueGeneration.current) return null;
      setQueue(result);
      setQueueState(result.items.length === 0 ? "empty" : "ready");
      return result;
    } catch (error) {
      if (generation !== queueGeneration.current) return null;
      if (preserveExistingOnError) {
        setQueueState("ready");
      } else {
        setQueue(null);
        setQueueState("error");
      }
      setQueueError(getCanonicalComplianceReviewErrorMessage(error));
      setQueueErrorKind(errorKind(error));
      return null;
    } finally {
      if (generation === queueGeneration.current) {
        queueInFlight.current = false;
      }
    }
  }, [
    contractorId,
    cursorHistory,
    hasOnboardingReviewableSubjects,
    organizationId,
    pageIndex,
    requirementKey,
    reviewClient,
    reviewStatus,
  ]);

  const loadManagedCompliance = useCallback(async () => {
    const parentScopeContractorId =
      onboardingContext?.parentContractorId
      ?? effectiveFixedContractorId
      ?? contractorId;
    if (
      !managedComplianceClient
      || !onboardingContext
      || !parentScopeContractorId
      || managedTargetUserIds.length === 0
    ) {
      setManagedComplianceByUserId(new Map());
      return;
    }
    const generation = ++managedComplianceGeneration.current;
    const entries = await Promise.all(
      managedTargetUserIds.map(async (userId) => {
        try {
          const members = await managedComplianceClient.getManagedCrew(
            parentScopeContractorId,
            userId,
          );
          const match = members.find((member) => member.userId === userId);
          return match ? [userId, match.compliance] as const : null;
        } catch {
          return null;
        }
      }),
    );
    if (generation !== managedComplianceGeneration.current) return;
    setManagedComplianceByUserId(
      new Map(
        entries.filter(
          (
            entry,
          ): entry is readonly [string, CanonicalIndividualComplianceRead] =>
            Boolean(entry),
        ),
      ),
    );
  }, [
    contractorId,
    effectiveFixedContractorId,
    managedComplianceClient,
    managedTargetUserIds,
    onboardingContext,
  ]);

  useEffect(() => {
    queueGeneration.current += 1;
    managedComplianceGeneration.current += 1;
    queueInFlight.current = false;
    if (!organizationId) {
      setQueue(null);
      setQueueState("selection_required");
      setQueueError("");
      setQueueErrorKind(null);
      setManagedComplianceByUserId(new Map());
      return;
    }
    if (!hasOnboardingReviewableSubjects) {
      setQueue(null);
      setQueueState("empty");
      setQueueError("");
      setQueueErrorKind(null);
      setManagedComplianceByUserId(new Map());
      return;
    }
    void loadQueue();
    void loadManagedCompliance();
    return () => {
      queueGeneration.current += 1;
      managedComplianceGeneration.current += 1;
      queueInFlight.current = false;
    };
  }, [
    hasOnboardingReviewableSubjects,
    loadManagedCompliance,
    loadQueue,
    organizationId,
  ]);

  useEffect(() => {
    if (!preview) return;
    const expiresAt = new Date(preview.expiresAt).getTime();
    const delay = Math.max(0, expiresAt - Date.now());
    const timer = window.setTimeout(
      () => setPreview(null),
      Math.min(delay, 2_147_483_647),
    );
    return () => window.clearTimeout(timer);
  }, [preview]);

  const resolveQueueIdentity = useCallback((
    item: CanonicalInternalComplianceReviewQueueItem,
  ): Readonly<{
    displayName: string;
    relationshipLabel: string | null;
    groupLabel: TrustedReviewSubject["groupLabel"] | null;
    reviewable: boolean;
  }> => {
    const serverLabel = safeDisplayLabel(item.personDisplayName);
    const trusted = item.targetPersonUserId
      ? trustedSubjectMap.get(item.targetPersonUserId)
      : undefined;
    const trustedForContext =
      !onboardingContext
      || Boolean(
        trusted
        && trusted.authoritySource !== "ambiguous",
      );
    return Object.freeze({
      displayName:
        serverLabel
        ?? (trustedForContext && trusted
          ? trusted.label
          : IDENTITY_UNAVAILABLE_LABEL),
      relationshipLabel: trusted?.relationshipLabel ?? null,
      groupLabel: trusted?.groupLabel ?? null,
      reviewable:
        Boolean(item.targetPersonUserId)
        && trustedForContext,
    });
  }, [onboardingContext, trustedSubjectMap]);

  const canReviewQueueItem = useCallback((
    item: CanonicalInternalComplianceReviewQueueItem,
  ): boolean =>
    item.source.source === "canonical"
    && Boolean(item.source.evidenceId)
    && resolveQueueIdentity(item).reviewable,
  [resolveQueueIdentity]);

  const loadSelection = useCallback(async (
    item: CanonicalInternalComplianceReviewQueueItem,
    options: Readonly<{ preserveDecisionDraft?: boolean }> = {},
  ) => {
    if (!canReviewQueueItem(item)) return;
    setSelectedItem(item);
    setDetail(null);
    setHistory(null);
    setDetailError("");
    setHistoryError("");
    setPreview(null);
    setPreviewError("");
    if (!options.preserveDecisionDraft) {
      setDecisionDraft(null);
      setReasonCode("");
      setNote("");
      setDecisionError("");
      setAttemptKey(null);
      setConflictRefreshing(false);
    }
    const generation = ++detailGeneration.current;
    if (!item.targetPersonUserId) {
      setDetailError(
        CANONICAL_COMPLIANCE_REVIEW_ERROR_MESSAGES.not_found,
      );
      return;
    }
    setDetailLoading(true);
    const evidenceId = item.source.source === "canonical"
      ? item.source.evidenceId
      : null;
    setHistoryLoading(Boolean(evidenceId));
    const detailPromise = reviewClient.getDetail({
      organizationId,
      ...(contractorId ? { contractorId } : {}),
      targetPersonUserId: item.targetPersonUserId,
    });
    const historyPromise = evidenceId
      ? reviewClient.getHistory({
          organizationId,
          ...(contractorId ? { contractorId } : {}),
          evidenceId,
        })
      : Promise.resolve(null);
    const [detailResult, historyResult] = await Promise.allSettled([
      detailPromise,
      historyPromise,
    ]);
    if (generation !== detailGeneration.current) return;
    if (detailResult.status === "fulfilled") {
      setDetail(detailResult.value);
    } else {
      setDetailError(
        getCanonicalComplianceReviewErrorMessage(detailResult.reason),
      );
    }
    if (historyResult.status === "fulfilled") {
      setHistory(historyResult.value);
    } else {
      setHistoryError(
        getCanonicalComplianceReviewErrorMessage(historyResult.reason),
      );
    }
    setDetailLoading(false);
    setHistoryLoading(false);
  }, [
    canReviewQueueItem,
    contractorId,
    organizationId,
    reviewClient,
  ]);

  async function refreshCurrentContext(preserveDecisionDraft = false) {
    const refreshedQueue = await loadQueue(true);
    if (!selectedItem) return;
    const refreshedItem = refreshedQueue?.items.find(
      (item) => queueItemKey(item) === queueItemKey(selectedItem),
    );
    if (preserveDecisionDraft) {
      await loadSelection(refreshedItem ?? selectedItem, {
        preserveDecisionDraft: true,
      });
    } else if (refreshedItem) {
      await loadSelection(refreshedItem);
    } else if (!refreshedQueue) {
      await loadSelection(selectedItem);
    } else if (refreshedQueue) {
      resetSelection();
    }
  }

  function handleOrganizationChange(value: string) {
    queueGeneration.current += 1;
    queueInFlight.current = false;
    setOrganizationId(value);
    setContractorId(effectiveFixedContractorId ?? "");
    setQueue(null);
    resetPaginationAndSelection();
  }

  const visibleQueueItems = queue?.items ?? [];
  const serverAuthorizedDelegatedTargetIds = new Set(
    visibleQueueItems
      .map((item) => item.targetPersonUserId)
      .filter((targetPersonUserId): targetPersonUserId is string => {
        if (!targetPersonUserId) return false;
        return trustedSubjectMap.get(targetPersonUserId)?.authoritySource
          === "delegation_candidate";
      }),
  );
  const unresolvedQueueItemCount = onboardingContext
    ? visibleQueueItems.filter(
      (item) => !resolveQueueIdentity(item).reviewable,
    ).length
    : 0;

  function renderRequirementRows(
    targetPersonUserId: string,
  ) {
    const subjectItems = visibleQueueItems.filter(
      (item) => item.targetPersonUserId === targetPersonUserId,
    );
    const managedRequirements = managedComplianceByUserId.get(targetPersonUserId)
      ?.requirements
      ?? [];
    const itemByRequirement = new Map(
      subjectItems.map((item) => [item.requirementKey, item]),
    );
    const managedRequirementByKey = new Map(
      managedRequirements.map((requirement) => [
        requirement.requirementKey,
        requirement,
      ]),
    );
    return (
      <dl className="mt-3 space-y-2">
        {CANONICAL_INTERNAL_COMPLIANCE_REVIEW_REQUIREMENT_KEYS.map((key) => {
          const item = itemByRequirement.get(key);
          const managedRequirement = managedRequirementByKey.get(key);
          return (
            <div
              key={key}
              className="rounded border bg-muted/20 px-2 py-1.5 text-xs"
            >
              <dt className="font-medium">
                {CANONICAL_COMPLIANCE_REQUIREMENT_LABELS[key]}
              </dt>
              <dd className="text-muted-foreground">
                {item
                  ? item.source.source === "none"
                    ? "No canonical submission"
                    : `${item.evidenceEligible ? "Eligible" : "Not eligible"} · ${CANONICAL_COMPLIANCE_REVIEW_STATE_LABELS[item.reviewState]} · ${item.operationallyVerified ? "Operationally verified" : "Not operationally verified"}`
                  : managedRequirement
                    ? requirementStatusLabel(managedRequirement)
                    : "No canonical submission"}
              </dd>
              {item && canReviewQueueItem(item) && (
                <dd className="mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void loadSelection(item)}
                  >
                    Review evidence
                  </Button>
                </dd>
              )}
            </div>
          );
        })}
      </dl>
    );
  }

  function renderSubjectGroup(
    title: string,
    subjects: readonly CanonicalOnboardingReviewSubject[],
  ) {
    if (subjects.length === 0) return null;
    return (
      <section className="space-y-2" aria-label={title}>
        <h4 className="text-sm font-medium">{title}</h4>
        <div className="grid gap-2 md:grid-cols-2">
          {subjects.map((subject) => {
            const subjectItems = visibleQueueItems.filter(
              (item) =>
                item.targetPersonUserId === subject.targetPersonUserId,
            );
            const managedRequirements = managedComplianceByUserId.get(
              subject.targetPersonUserId,
            )?.requirements ?? [];
            const verifiedCount = subjectItems.filter(
              (item) => item.operationallyVerified,
            ).length;
            const managedVerifiedCount = managedRequirements.filter(
              (requirement) => requirement.operationallyVerified,
            ).length;
            return (
              <div
                key={`${title}:${subject.contractorId}:${subject.targetPersonUserId}`}
                className="rounded-md border p-3"
              >
                <div className="font-medium">{subject.label}</div>
                <div className="text-xs text-muted-foreground">
                  {subject.relationshipLabel} · {subject.roleLabel}
                </div>
                <div className="mt-2 text-xs">
                  {subjectItems.length > 0
                    ? `${verifiedCount} of ${CANONICAL_INTERNAL_COMPLIANCE_REVIEW_REQUIREMENT_KEYS.length} operationally verified`
                    : managedRequirements.length > 0
                      ? `${managedVerifiedCount} of ${managedRequirements.length} operationally verified`
                      : "No canonical requirement rows returned."}
                </div>
                {renderRequirementRows(subject.targetPersonUserId)}
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  function renderRelatedContractors() {
    if (!onboardingContext?.relatedContractors.length) return null;
    return (
      <section
        className="space-y-2"
        aria-label="Subcontractors / Sub-parents"
      >
        <h4 className="text-sm font-medium">
          Subcontractors / Sub-parents
          {" "}
          ({onboardingContext.relatedContractors.length})
        </h4>
        <div className="space-y-2">
          {onboardingContext.relatedContractors.map((contractor) => {
            const contractorHasDelegatedPerson = contractor.linkedPeople.some(
              (person) =>
                person.userId
                ? serverAuthorizedDelegatedTargetIds.has(person.userId)
                : false,
            );
            return (
              <div
                key={contractor.contractorId}
                className="rounded-md border p-3"
                style={{
                  marginLeft: `${Math.min(Math.max(contractor.depth - 1, 0), 4) * 16}px`,
                }}
                data-testid="canonical-related-contractor"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="font-medium">{contractor.contractorName}</div>
                    <div className="text-xs text-muted-foreground">
                      {contractor.relationshipLabel}
                      {" · "}
                      Parent: {contractor.parentContractorName}
                    </div>
                  </div>
                  <Badge variant="outline">
                    {contractorHasDelegatedPerson
                      ? "Delegated for parent review"
                      : contractor.reviewability === "reviewable"
                        ? "Canonical review available"
                        : contractor.reviewability === "blocked_scope"
                          ? "Website scope required"
                          : contractor.reviewability === "informational_only"
                            ? "Informational only"
                            : "Ambiguous relationship"}
                  </Badge>
                </div>
                <div className="mt-3 space-y-3">
                  {contractor.linkedPeople.map((person, index) => {
                    const delegatedForParentReview = Boolean(
                      person.userId
                      && serverAuthorizedDelegatedTargetIds.has(person.userId),
                    );
                    return (
                      <div
                        key={`${contractor.contractorId}:${person.userId ?? "informational"}:${index}`}
                        className="rounded-md border border-dashed p-3"
                      >
                        <div className="font-medium">{person.displayName}</div>
                        <div className="text-xs text-muted-foreground">
                          {person.roleLabel ?? "Individual identity unavailable"}
                          {person.childRosterActive
                            ? " · Active child roster"
                            : ""}
                          {person.parentRosterActive
                            ? " · Active parent roster"
                            : ""}
                          {delegatedForParentReview
                            ? " · Delegated for parent review"
                            : ""}
                        </div>
                        {(person.canReviewCanonically || delegatedForParentReview)
                        && person.userId
                          ? renderRequirementRows(person.userId)
                          : (
                            <Alert className="mt-3">
                              <ShieldAlert className="h-4 w-4" />
                              <AlertTitle>
                                Canonical review unavailable
                              </AlertTitle>
                              <AlertDescription>
                                {person.blockerMessage}
                                {person.blockerCode && (
                                  <>
                                    {" "}
                                    <span className="font-mono text-xs">
                                      {person.blockerCode}
                                    </span>
                                  </>
                                )}
                              </AlertDescription>
                            </Alert>
                          )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  function handleContractorChange(value: string) {
    queueGeneration.current += 1;
    queueInFlight.current = false;
    setContractorId(value === ALL_FILTER_VALUE ? "" : value);
    setQueue(null);
    resetPaginationAndSelection();
  }

  function handleFilterChange(
    filter: "requirement" | "status",
    value: string,
  ) {
    queueGeneration.current += 1;
    queueInFlight.current = false;
    if (filter === "requirement") {
      setRequirementKey(
        value === ALL_FILTER_VALUE
          ? ""
          : value as CanonicalInternalComplianceReviewRequirementKey,
      );
    } else {
      setReviewStatus(
        value === ALL_FILTER_VALUE
          ? ""
          : value as CanonicalInternalComplianceReviewState,
      );
    }
    setQueue(null);
    resetPaginationAndSelection();
  }

  function goToNextPage() {
    if (!queue?.nextCursor) return;
    setCursorHistory((current) => [
      ...current.slice(0, pageIndex + 1),
      queue.nextCursor,
    ]);
    setPageIndex((current) => current + 1);
    resetSelection();
  }

  function goToPreviousPage() {
    if (pageIndex === 0) return;
    setPageIndex((current) => current - 1);
    resetSelection();
  }

  async function handlePreview(
    item: CanonicalInternalComplianceReviewQueueItem,
  ) {
    if (
      previewLoading
      || item.source.source !== "canonical"
      || !item.source.canPreview
      || !canReviewQueueItem(item)
    ) {
      return;
    }
    const generation = ++previewGeneration.current;
    setPreviewLoading(true);
    setPreviewError("");
    setPreview(null);
    try {
      const result = await previewClient.getPreview({
        organizationId,
        ...(contractorId ? { contractorId } : {}),
        evidenceId: item.source.evidenceId,
      });
      if (generation === previewGeneration.current) setPreview(result);
    } catch (error) {
      if (generation === previewGeneration.current) {
        setPreviewError(getCanonicalComplianceReviewErrorMessage(error));
      }
    } finally {
      if (generation === previewGeneration.current) setPreviewLoading(false);
    }
  }

  function openDecision(
    item: CanonicalInternalComplianceReviewQueueItem,
    decision: CanonicalInternalComplianceReviewDecision,
  ) {
    if (
      item.source.source !== "canonical"
      || !item.evidenceEligible
      || !canReviewQueueItem(item)
      || item.reviewState === decision
    ) {
      return;
    }
    setDecisionDraft({
      evidenceId: item.source.evidenceId,
      requirementKey: item.requirementKey,
      decision,
    });
    setReasonCode("");
    setNote("");
    setDecisionError("");
    setAttemptKey(null);
    setConflictRefreshing(false);
  }

  function closeDecision() {
    if (submitting) return;
    resetDecisionDraft();
  }

  function resetDecisionDraft() {
    setDecisionDraft(null);
    setReasonCode("");
    setNote("");
    setDecisionError("");
    setAttemptKey(null);
    setConflictRefreshing(false);
  }

  async function handleDecisionSubmit() {
    if (!decisionDraft || submitting || conflictRefreshing) return;
    let normalized: Readonly<{ reasonCode?: string; note?: string }>;
    try {
      normalized = validateCanonicalComplianceDecisionDraft({
        decision: decisionDraft.decision,
        reasonCode,
        note,
      });
    } catch {
      setDecisionError(
        reasonCode === "other" && !note.trim()
          ? "A note is required when Other is selected."
          : "Review the reason and note before submitting.",
      );
      return;
    }
    let submissionKey = attemptKey;
    try {
      submissionKey ??= createIdempotencyKey();
    } catch {
      setDecisionError("A secure submission key could not be created.");
      return;
    }
    setAttemptKey(submissionKey);
    setSubmitting(true);
    setDecisionError("");
    const currentRequirement = detail?.requirements.find(
      (item) =>
        item.requirementKey === decisionDraft.requirementKey
        && item.source.source === "canonical"
        && item.source.evidenceId === decisionDraft.evidenceId,
    );
    try {
      const result = await reviewClient.submitDecision({
        organizationId,
        ...(contractorId ? { contractorId } : {}),
        evidenceId: decisionDraft.evidenceId,
        decision: decisionDraft.decision,
        ...normalized,
        idempotencyKey: submissionKey,
        expectedLatestDecisionId: currentRequirement?.latestDecisionId ?? null,
      });
      const completedDecision = decisionDraft.decision;
      resetDecisionDraft();
      await refreshCurrentContext();
      const applyConfirmedDecision = (
        item: CanonicalInternalComplianceReviewQueueItem,
      ): CanonicalInternalComplianceReviewQueueItem =>
        item.source.source === "canonical"
        && item.source.evidenceId === result.evidenceId
          ? {
              ...item,
              reviewState: result.reviewState,
              latestDecisionId: result.decisionId,
              operationallyVerified: result.operationallyVerified,
            }
          : item;
      setQueue((current) => {
        const base = current ?? queue;
        return base
          ? {
              ...base,
              items: base.items.map(applyConfirmedDecision),
            }
          : base;
      });
      setSelectedItem((current) =>
        current ? applyConfirmedDecision(current) : current
      );
      setDetail((current) =>
        current
          ? {
              ...current,
              requirements: current.requirements.map(applyConfirmedDecision),
            }
          : current
      );
      toast({
        title:
          completedDecision === "approved"
            ? "Compliance evidence approved"
            : completedDecision === "rejected"
            ? "Compliance evidence rejected"
            : "Changes requested",
        description:
          completedDecision === "approved" && !result.operationallyVerified
            ? "Approval was saved. Additional evidence conditions still prevent operational verification."
            : "Canonical review data was refreshed.",
      });
    } catch (error) {
      const kind = errorKind(error);
      if (kind === "stale_decision" || kind === "idempotency_conflict") {
        setAttemptKey(null);
        setDecisionError(getCanonicalComplianceReviewErrorMessage(error));
        setConflictRefreshing(true);
        try {
          await refreshCurrentContext(true);
        } finally {
          setConflictRefreshing(false);
        }
      } else {
        setDecisionError(getCanonicalComplianceReviewErrorMessage(error));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="space-y-4"
      data-testid="canonical-individual-compliance-review-panel"
    >
      <Alert>
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>Canonical Individual Compliance</AlertTitle>
        <AlertDescription>
          Canonical reviews use immutable evidence and append-only review
          decisions. Legacy compliance remains separate.
          {scopeLabel ? ` ${scopeLabel}` : ""}
        </AlertDescription>
      </Alert>

      {onboardingContext && (
        <Card data-testid="canonical-onboarding-parent-context">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Parent-scoped review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2 text-sm md:grid-cols-2">
              <div>
                <span className="font-medium">Parent: </span>
                {onboardingContext.parentContractorName}
              </div>
              <div>
                <span className="font-medium">Organization: </span>
                {onboardingContext.organizationLabel}
              </div>
            </div>
            {renderSubjectGroup(
              "Parent Personnel",
              onboardingContext.parentPersonnel,
            )}
            {renderRelatedContractors()}
            {renderSubjectGroup("Crew", onboardingContext.crew)}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Review scope and filters</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-4">
          {!onboardingContext && <div className="space-y-1">
            <Label htmlFor="canonical-review-organization">Organization</Label>
            <select
              id="canonical-review-organization"
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={organizationId}
              onChange={(event) =>
                handleOrganizationChange(event.currentTarget.value)
              }
            >
              <option value="">Select organization</option>
              {organizations.map((organization) => (
                <option key={organization.id} value={organization.id}>
                  {organization.label}
                </option>
              ))}
            </select>
          </div>}
          {!onboardingContext && <div className="space-y-1">
            {effectiveFixedContractorId ? (
              <>
                <Label>Parent contractor scope</Label>
                <div
                  className="flex h-10 items-center rounded-md border bg-muted/30 px-3 text-sm"
                  data-testid="canonical-review-fixed-contractor"
                >
                  {contractors.find(
                    (contractor) =>
                      contractor.id === effectiveFixedContractorId,
                  )?.companyName ?? "Selected parent contractor"}
                </div>
              </>
            ) : (
              <>
                <Label htmlFor="canonical-review-contractor">Contractor</Label>
                <select
                  id="canonical-review-contractor"
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                  value={contractorId || ALL_FILTER_VALUE}
                  disabled={!organizationId}
                  onChange={(event) =>
                    handleContractorChange(event.currentTarget.value)
                  }
                >
                  <option value={ALL_FILTER_VALUE}>All contractors</option>
                  {contractorOptions.map((contractor) => (
                    <option key={contractor.id} value={contractor.id}>
                      {contractor.companyName}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>}
          <div className="space-y-1">
            <Label htmlFor="canonical-review-requirement">Requirement</Label>
            <select
              id="canonical-review-requirement"
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={requirementKey || ALL_FILTER_VALUE}
              disabled={!organizationId}
              onChange={(event) =>
                handleFilterChange("requirement", event.currentTarget.value)
              }
            >
              <option value={ALL_FILTER_VALUE}>All requirements</option>
              {CANONICAL_INTERNAL_COMPLIANCE_REVIEW_REQUIREMENT_KEYS.map(
                (requirement) => (
                  <option key={requirement} value={requirement}>
                    {CANONICAL_COMPLIANCE_REQUIREMENT_LABELS[requirement]}
                  </option>
                ),
              )}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="canonical-review-status">Review status</Label>
            <select
              id="canonical-review-status"
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={reviewStatus || ALL_FILTER_VALUE}
              disabled={!organizationId}
              onChange={(event) =>
                handleFilterChange("status", event.currentTarget.value)
              }
            >
              <option value={ALL_FILTER_VALUE}>All review states</option>
              {CANONICAL_INTERNAL_COMPLIANCE_REVIEW_STATES.map((state) => (
                <option key={state} value={state}>
                  {CANONICAL_COMPLIANCE_REVIEW_STATE_LABELS[state]}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Canonical review queue</CardTitle>
          <Button
            size="sm"
            variant="outline"
            disabled={!organizationId || queueState === "loading"}
            onClick={() => void loadQueue()}
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {queueState === "selection_required" && (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Select an organization to load its authorized review queue.
            </div>
          )}
          {queueState === "loading" && (
            <div
              className="flex items-center justify-center gap-2 py-10 text-sm"
              role="status"
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading canonical review queue…
            </div>
          )}
          {queueState === "empty" && (
            <div className="py-10 text-center">
              <FileQuestion className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
              <p className="text-sm font-medium">No canonical reviews found</p>
              <p className="text-xs text-muted-foreground">
                No requirements match the selected scope and filters.
              </p>
            </div>
          )}
          {queueState === "error" && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{queueErrorTitle(queueErrorKind)}</AlertTitle>
              <AlertDescription>
                {queueError} No legacy approval fallback was attempted.
              </AlertDescription>
            </Alert>
          )}
          {queueState === "ready" && queue && (
            <>
              {unresolvedQueueItemCount > 0 && (
                <Alert className="mb-3">
                  <ShieldAlert className="h-4 w-4" />
                  <AlertTitle>Parent context mismatch</AlertTitle>
                  <AlertDescription>
                    {unresolvedQueueItemCount} authorized queue row(s) could
                    not be matched to the trusted onboarding subject context.
                    Their identity and review actions remain unavailable.
                  </AlertDescription>
                </Alert>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Person</TableHead>
                    <TableHead>Requirement</TableHead>
                    <TableHead>Document</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Evidence status</TableHead>
                    <TableHead>Review status</TableHead>
                    <TableHead>Operational status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleQueueItems.map((item) => {
                    const identity = resolveQueueIdentity(item);
                    return (
                      <TableRow key={queueItemKey(item)}>
                        <TableCell>
                          <div>{identity.displayName}</div>
                          {identity.relationshipLabel && (
                            <div className="text-xs text-muted-foreground">
                              {identity.groupLabel} · {identity.relationshipLabel}
                            </div>
                          )}
                          {!identity.reviewable && onboardingContext && (
                            <div className="text-xs text-destructive">
                              Trusted onboarding identity unavailable
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {CANONICAL_COMPLIANCE_REQUIREMENT_LABELS[
                            item.requirementKey
                          ]}
                        </TableCell>
                        <TableCell>
                          <div>{sourceLabel(item)}</div>
                          <div className="text-xs text-muted-foreground">
                            {item.fileName ?? "No document"}
                          </div>
                        </TableCell>
                        <TableCell>{formatDate(item.submittedAt)}</TableCell>
                        <TableCell>{formatDate(item.expiresAt)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {item.evidenceEligible ? "Eligible" : "Not eligible"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {CANONICAL_COMPLIANCE_REVIEW_STATE_LABELS[
                              item.reviewState
                            ]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {item.operationallyVerified
                              ? "Operationally verified"
                              : "Not operationally verified"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {canReviewQueueItem(item) ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => void loadSelection(item)}
                            >
                              Review evidence
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {item.source.source === "none"
                                ? "No canonical submission"
                                : "Review unavailable"}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <div className="mt-3 flex items-center justify-between">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={pageIndex === 0 || queueState === "loading"}
                  onClick={goToPreviousPage}
                >
                  <ChevronLeft className="mr-1 h-3.5 w-3.5" />
                  Previous
                </Button>
                <span className="text-xs text-muted-foreground">
                  Page {pageIndex + 1}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!queue.nextCursor || queueState === "loading"}
                  onClick={goToNextPage}
                >
                  Next
                  <ChevronRight className="ml-1 h-3.5 w-3.5" />
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Sheet
        open={Boolean(selectedItem)}
        onOpenChange={(open) => {
          if (!open) resetSelection();
        }}
      >
        <SheetContent className="w-full overflow-y-auto sm:max-w-3xl">
          <SheetHeader>
            <SheetTitle>Canonical compliance review detail</SheetTitle>
            <SheetDescription>
              Evidence eligibility, review approval, and operational verification
              are separate states.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-5 space-y-5">
            {selectedItem && (
              <dl className="grid gap-3 rounded-md border p-3 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-muted-foreground">Person</dt>
                  <dd>{resolveQueueIdentity(selectedItem).displayName}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Relationship</dt>
                  <dd>
                    {resolveQueueIdentity(selectedItem).groupLabel
                      ?? "Authorized internal review"}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Requirement</dt>
                  <dd>
                    {CANONICAL_COMPLIANCE_REQUIREMENT_LABELS[
                      selectedItem.requirementKey
                    ]}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Selected document</dt>
                  <dd>{selectedItem.fileName ?? "No canonical submission"}</dd>
                </div>
              </dl>
            )}
            {detailLoading && (
              <div className="flex items-center gap-2 text-sm" role="status">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading review detail…
              </div>
            )}
            {detailError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Detail unavailable</AlertTitle>
                <AlertDescription>{detailError}</AlertDescription>
              </Alert>
            )}
            {detail && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold">
                  Five canonical requirements
                </h3>
                {detail.requirements.map((item) => {
                  const isExactSelectedEvidence =
                    selectedItem?.source.source === "canonical"
                    && item.source.source === "canonical"
                    && selectedItem.source.evidenceId === item.source.evidenceId;
                  return (
                    <div
                      key={item.requirementKey}
                      className="space-y-2 rounded-md border p-3"
                    >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-medium">
                        {CANONICAL_COMPLIANCE_REQUIREMENT_LABELS[
                          item.requirementKey
                        ]}
                      </p>
                      <Badge variant="outline">{sourceLabel(item)}</Badge>
                    </div>
                    <dl className="grid gap-2 text-xs sm:grid-cols-2">
                      <div>
                        <dt className="text-muted-foreground">Document</dt>
                        <dd>{item.fileName ?? "No canonical submission"}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">File details</dt>
                        <dd>
                          {item.mimeType ?? "—"} · {formatBytes(item.byteSize)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Submitted</dt>
                        <dd>{formatDate(item.submittedAt)}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Expires</dt>
                        <dd>{formatDate(item.expiresAt)}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Evidence eligibility</dt>
                        <dd>{item.evidenceEligible ? "Eligible" : "Not eligible"}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Review state</dt>
                        <dd>
                          {CANONICAL_COMPLIANCE_REVIEW_STATE_LABELS[
                            item.reviewState
                          ]}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Operational status</dt>
                        <dd>
                          {item.operationallyVerified
                            ? "Operationally verified"
                            : "Not operationally verified"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Failure reason</dt>
                        <dd>
                          {item.operationallyVerified
                            ? "—"
                            : formatFailureReason(item.failureReason)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Latest decision</dt>
                        <dd>
                          {item.latestDecisionId
                            ? "Decision recorded"
                            : "No decision"}
                        </dd>
                      </div>
                    </dl>
                    {isExactSelectedEvidence
                      && item.reviewState === "approved"
                      && !item.operationallyVerified && (
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>
                          Approved, but not operationally verified
                        </AlertTitle>
                        <AlertDescription>
                          The approval is recorded. Operational verification
                          remains blocked because{" "}
                          {formatFailureReason(item.failureReason).toLowerCase()}
                          .
                        </AlertDescription>
                      </Alert>
                    )}
                    {isExactSelectedEvidence && (
                      <div className="flex flex-wrap gap-2">
                        {item.source.canPreview && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={previewLoading}
                            onClick={() => void handlePreview(item)}
                          >
                            {previewLoading
                              ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                              : <Eye className="mr-1 h-3.5 w-3.5" />}
                            Preview
                          </Button>
                        )}
                        {item.evidenceEligible ? (
                          <>
                            <Button
                              size="sm"
                              disabled={item.reviewState === "approved"}
                              onClick={() => openDecision(item, "approved")}
                            >
                              <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                              {item.reviewState === "approved"
                                ? "Approved"
                                : "Approve"}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => openDecision(item, "rejected")}
                            >
                              <XCircle className="mr-1 h-3.5 w-3.5" />
                              Reject
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                openDecision(item, "changes_requested")
                              }
                            >
                              Request Changes
                            </Button>
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            Decisions are unavailable for ineligible evidence.
                          </span>
                        )}
                      </div>
                    )}
                    {!isExactSelectedEvidence
                      && canReviewQueueItem(item) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void loadSelection(item)}
                      >
                        Review this evidence
                      </Button>
                    )}
                    </div>
                  );
                })}
              </div>
            )}

            {previewError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Preview unavailable</AlertTitle>
                <AlertDescription>{previewError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Immutable decision history</h3>
              {historyLoading && (
                <div className="flex items-center gap-2 text-sm" role="status">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading decision history…
                </div>
              )}
              {historyError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>History unavailable</AlertTitle>
                  <AlertDescription>{historyError}</AlertDescription>
                </Alert>
              )}
              {history && history.items.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No review decisions have been recorded for this evidence.
                </p>
              )}
              {history?.items.map((entry) => (
                <div key={entry.decisionId} className="rounded-md border p-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">
                      {CANONICAL_COMPLIANCE_REVIEW_STATE_LABELS[entry.decision]}
                    </span>
                    <time className="text-xs text-muted-foreground">
                      {formatDate(entry.decidedAt)}
                    </time>
                  </div>
                  <p className="mt-1 text-xs">
                    Reason: {getCanonicalComplianceReasonLabel(entry.reasonCode)}
                  </p>
                  {entry.note && (
                    <p className="mt-1 whitespace-pre-wrap text-xs">{entry.note}</p>
                  )}
                  <p className="mt-2 text-xs text-muted-foreground">
                    {entry.reviewerDisplayName ?? "Internal reviewer"} ·{" "}
                    {entry.reviewerRole} audit snapshot
                  </p>
                  {entry.previousDecisionId && (
                    <p className="text-xs text-muted-foreground">
                      Supersedes a previous immutable decision.
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog
        open={Boolean(preview)}
        onOpenChange={(open) => {
          if (!open) {
            previewGeneration.current += 1;
            setPreview(null);
          }
        }}
      >
        <DialogContent className="max-h-[92vh] max-w-5xl overflow-hidden">
          <DialogHeader>
            <DialogTitle>{preview?.fileName ?? "Evidence preview"}</DialogTitle>
            <DialogDescription>
              This private preview is short-lived and does not indicate
              operational verification.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            preview.mimeType === "application/pdf"
            || preview.mimeType.startsWith("image/")
          ) ? (
            preview.mimeType.startsWith("image/") ? (
              <div className="max-h-[70vh] overflow-auto rounded-md border">
                <img
                  src={preview.signedUrl}
                  alt={preview.fileName}
                  className="mx-auto max-h-[70vh] object-contain"
                />
              </div>
            ) : (
              <iframe
                src={preview.signedUrl}
                title={preview.fileName}
                className="h-[70vh] w-full rounded-md border"
              />
            )
          ) : preview ? (
            <div className="space-y-3 rounded-md border p-4">
              <p className="text-sm">
                This file type is available through a secure short-lived link.
              </p>
              <Button
                onClick={() =>
                  window.open(
                    preview.signedUrl,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                Open securely
              </Button>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)}>
              Close preview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(decisionDraft)}
        onOpenChange={(open) => {
          if (!open) closeDecision();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {decisionDraft
                ? CANONICAL_COMPLIANCE_DECISION_LABELS[decisionDraft.decision]
                : "Review decision"}
            </DialogTitle>
            <DialogDescription>
              Confirm an append-only decision for this exact immutable evidence.
            </DialogDescription>
          </DialogHeader>

          {decisionDraft?.decision !== "approved" && (
            <div className="space-y-1">
              <Label htmlFor="canonical-review-reason">Reason</Label>
              <select
                id="canonical-review-reason"
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={reasonCode}
                onChange={(event) =>
                  setReasonCode(
                    event.currentTarget.value as
                      | CanonicalComplianceReviewReasonCode
                      | "",
                  )
                }
              >
                <option value="">Select reason</option>
                {CANONICAL_COMPLIANCE_REVIEW_REASONS.map((reason) => (
                  <option key={reason.code} value={reason.code}>
                    {reason.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="canonical-review-note">
                Reviewer note
                {reasonCode === "other" ? " (required)" : " (optional)"}
              </Label>
              <span className="text-xs text-muted-foreground">
                {remainingCanonicalComplianceNoteCharacters(note)} remaining
              </span>
            </div>
            <Textarea
              id="canonical-review-note"
              value={note}
              maxLength={CANONICAL_INTERNAL_COMPLIANCE_NOTE_MAX_LENGTH}
              onChange={(event) => setNote(event.currentTarget.value)}
              rows={4}
              placeholder="Plain text only. Do not include URLs or credentials."
            />
          </div>

          {decisionError && (
            <Alert variant="destructive" aria-live="assertive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Decision not submitted</AlertTitle>
              <AlertDescription>{decisionError}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button variant="outline" disabled={submitting} onClick={closeDecision}>
              Cancel
            </Button>
            <Button
              disabled={submitting || conflictRefreshing}
              onClick={() => void handleDecisionSubmit()}
            >
              {(submitting || conflictRefreshing) && (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              )}
              {attemptKey && decisionError && !conflictRefreshing
                ? "Retry same submission"
                : "Confirm decision"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
